import { getThreadMessageGroupCollectionPath, ThreadMessageGroup, ThreadType } from '@hb/shared'
import {
  collection,
  CollectionReference,
  documentId,
  endAt,
  orderBy,
  query,
  startAt,
} from 'firebase/firestore'
import { useMemo } from 'react'
import { db } from '../../../backend/db'
import { useQuery } from '../useQuery'

export interface FlatThreadMessage {
  id: string
  text: string
  createdOn: number // Unix ms
  createdBy: string // userId of sender
}

export const usePatientThreadMessages = (
  patientId: string | null | undefined,
  startDateStr: string | null, // YYYY-MM-DD inclusive
  endDateStr: string | null, // YYYY-MM-DD inclusive
): FlatThreadMessage[] => {
  const colPath = useMemo(
    () => (patientId ? getThreadMessageGroupCollectionPath(ThreadType.PATIENT, patientId) : null),
    [patientId],
  )

  const ref = useMemo(() => {
    if (!colPath || !startDateStr || !endDateStr) return null
    const col = collection(db, colPath) as CollectionReference<ThreadMessageGroup>
    return query(col, orderBy(documentId()), startAt(startDateStr), endAt(endDateStr))
  }, [colPath, startDateStr, endDateStr])

  const { dataArr: groups } = useQuery<ThreadMessageGroup>(ref)

  return useMemo(() => {
    if (!groups) return []
    const flat: FlatThreadMessage[] = []
    for (const group of groups) {
      for (const [msgId, msg] of Object.entries(group.messages ?? {})) {
        if (msg.deletedOn) continue
        flat.push({
          id: msgId,
          text: msg.text ?? '',
          createdOn: msg.createdOn,
          createdBy: msg.createdBy,
        })
      }
    }
    return flat.sort((a, b) => a.createdOn - b.createdOn)
  }, [groups])
}
